import React, { useEffect, useState } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Heading1,
  Heading2,
  Heading3,
} from "lucide-react";
import { Button } from "@/shared/ui/button"
import { cn } from "@/lib/utils"

function ToolbarButton({ active, disabled, onClick, label, children }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "h-9 w-9 rounded-md border border-transparent hover:bg-slate-50 hover:border-[#E2E4E9]",
        active && "bg-slate-100 border-[#E2E4E9]"
      )}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={!!active}
      aria-label={label}
      title={label}
    >
      {children}
    </Button>
  );
}

export default function RichTextToolbar({ editor }) {
  if (!editor) return null;

  // Ensure toolbar rerenders when selection/formatting changes.
  const [, forceRerender] = useState(0);
  const [flashMark, setFlashMark] = useState(null);
  useEffect(() => {
    if (!editor) return;
    const onChange = () => forceRerender((x) => x + 1);
    editor.on("selectionUpdate", onChange);
    editor.on("transaction", onChange);
    editor.on("update", onChange);
    return () => {
      editor.off("selectionUpdate", onChange);
      editor.off("transaction", onChange);
      editor.off("update", onChange);
    };
  }, [editor]);

  const isFocused = editor.isFocused;
  const markActive = (mark) => flashMark === mark || (isFocused && editor.isActive(mark));

  const flash = (mark) => {
    setFlashMark(mark);
    window.setTimeout(() => setFlashMark((prev) => (prev === mark ? null : prev)), 120);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-xl border border-[#E4E4E7] bg-white p-2">
      <ToolbarButton
        label="Bold"
        active={markActive("bold")}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        onClick={() => {
          if (editor.state.selection.empty) {
            flash("bold");
            editor.chain().focus().toggleBold().run();
            editor.chain().focus().unsetBold().run();
            return;
          }
          editor.chain().focus().toggleBold().run();
        }}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        label="Italic"
        active={markActive("italic")}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        onClick={() => {
          if (editor.state.selection.empty) {
            flash("italic");
            editor.chain().focus().toggleItalic().run();
            editor.chain().focus().unsetItalic().run();
            return;
          }
          editor.chain().focus().toggleItalic().run();
        }}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <div className="mx-1 h-6 w-px bg-[#E4E4E7]" />

      <ToolbarButton
        label="Heading 1"
        active={isFocused && editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={isFocused && editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 3"
        active={isFocused && editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>

      <div className="mx-1 h-6 w-px bg-[#E4E4E7]" />

      <ToolbarButton
        label="Bullet list"
        active={isFocused && editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        label="Ordered list"
        active={isFocused && editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <div className="mx-1 h-6 w-px bg-[#E4E4E7]" />

      <ToolbarButton
        label="Undo"
        disabled={!editor.can().chain().focus().undo().run()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        label="Redo"
        disabled={!editor.can().chain().focus().redo().run()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

